<template>
  <ProductNoveltyList :novelty-items="items" />
</template>

<script setup lang="ts">
import { NoveltyItem, DisplayNoveltyItem, NoveltySku } from "~/types/novelty"

const props = defineProps<{
  noveltyItems?: NoveltyItem[]
}>()

const items = computed((): DisplayNoveltyItem[] => {
  const items: DisplayNoveltyItem[] = []

  // プレゼントされるノベルティを商品ごとにまとめる
  props.noveltyItems?.forEach((noveltyItem) => {
    const noveltyPattern = getNoveltyPattern(noveltyItem)
    noveltyPattern?.novelty_skus.forEach((noveltySku) => {
      const main_image = noveltySku.skus[0].main_image
      const item_name = getNoveltyProductName(noveltySku.skus)
      const campaign_name = noveltyPattern.public_name || ""

      const findItem = items.find(
        (item) =>
          item.main_image === main_image && item.item_name === item_name && item.campaign_name === campaign_name,
      )
      if (findItem) {
        findItem.quantity += 1
      } else {
        const supplements = []
        if (noveltyPattern.amount_gte > 1) {
          supplements.push(`※${getNoveltyAmountText(noveltyPattern)}ご購入の方限定`)
        }
        const sortNumber = getNoveltyPatternSortNumber(noveltyItem)
        if (sortNumber > 0) {
          noveltyItem.novelty_patterns
            .sort((a, b) => b.sort - a.sort)
            .filter((novelty_pattern) => novelty_pattern.sort < sortNumber)
            .forEach((noveltyPattern) => {
              supplements.push(`※${getNoveltyUpgradeText(noveltyPattern)}`)
            })
        }

        if (noveltyPattern.note) {
          // noteを改行で区切る
          noveltyPattern.note.split(/\r\n|\n/).forEach((note) => {
            supplements.push(`${note}`)
          })
        }

        items.push({
          main_image,
          item_name,
          campaign_name,
          supplements,
          quantity: 1,
        })
      }
    })
  })

  return items
})

const getNoveltyPattern = (item: NoveltyItem) => {
  return item.novelty_patterns.find((novelty_pattern) => novelty_pattern.sort === item.novelty_pattern_sort_number)
}

const getNoveltyProductName = (skus: NoveltySku[]) => {
  // product_idでまとめる
  const productMap: Record<string, NoveltySku[]> = {}
  skus.forEach((sku) => {
    if (!productMap[sku.product_id]) {
      productMap[sku.product_id] = []
    }
    productMap[sku.product_id].push(sku)
  })

  if (Object.keys(productMap).length === 1) {
    return productMap[skus[0].product_id][0].product_name
  } else {
    const productNames: string[] = []
    Object.keys(productMap).forEach((productId) => {
      const productSkus = productMap[productId]
      productNames.push(productSkus[0].product_name)
    })
    return `${productNames.join("、")} の中から一つ`
  }
}

const getNoveltyAmountText = (noveltyPattern: NoveltyItem["novelty_patterns"][number] | undefined) => {
  if (!noveltyPattern) {
    return ""
  }
  if (noveltyPattern.amount_lte) {
    return `${(noveltyPattern.amount_gte ?? 0).toLocaleString()}〜${(
      noveltyPattern.amount_lte ?? 0
    ).toLocaleString()}円`
  } else {
    return `${(noveltyPattern.amount_gte ?? 0).toLocaleString()}円以上`
  }
}

const getNoveltyPatternSortNumber = (item: NoveltyItem) => {
  return getNoveltyPattern(item)?.sort || 0
}

const getNoveltyUpgradeText = (noveltyPattern: NoveltyItem["novelty_patterns"][number]) => {
  const skus = noveltyPattern.novelty_skus.map((novelty_sku) => getNoveltyProductName(novelty_sku.skus)).join(", ")
  return `${getNoveltyAmountText(noveltyPattern)}ご購入で${skus}に替わります`
}
</script>
